import { apiClient } from './apiClient';

export enum DocumentCategory {
  InitialPhotos = 1,
  DesignRenders = 2,
  Contracts = 3,
  Quotes = 4,
  Measurements = 5,
  InstallationPhotos = 6,
  Correspondence = 7,
  TechnicalSpecs = 8,
  FormDocument = 9,
  Other = 10
}

export enum DocumentType {
  Uploaded = 1,
  FormPdf = 2
}

export enum StorageTier {
  Hot = 1,
  Cool = 2,
  Archive = 3
}

export interface JobDocument {
  id: string;
  jobId: string;
  businessId: string;
  fileName: string;
  originalFileName: string;
  fileExtension: string;
  mimeType: string;
  category: DocumentCategory;
  documentType: DocumentType;
  storageTier: StorageTier;
  fileSizeBytes: number;
  uploadedDate: string;
  uploadedById?: string;
  uploadedByName?: string;
  description?: string;
  version: number;
  hasThumbnail: boolean;
  thumbnailBlobName?: string;
  formId?: number;
  formName?: string;
  formDisplayOrder?: number;
}

export interface UploadConstraints {
  maxFileSizeBytes: number;
  maxFileSizeMb: number;
  maxTotalJobSizeBytes: number;
  maxTotalJobSizeMb: number;
  allowedExtensions: string[];
}

export interface UploadDocumentDto {
  jobId: string;
  category?: DocumentCategory;
  description?: string;
}

export interface UpdateDocumentDto {
  id: string;
  category?: DocumentCategory;
  description?: string;
}

export interface StoreFormPdfDto {
  jobId: string;
  formId: number;
  formName: string;
  jobNumber: string;
  pdfContent: string; // Base64 encoded PDF content
}

export interface BatchUploadFileDto {
  file: File;
  category: DocumentCategory;
  description?: string;
}

export interface BatchUploadDocumentsDto {
  jobId: string;
  files: BatchUploadFileDto[];
}

export interface FormPdfFreshness {
  formId: number;
  formName: string;
  hasPdf: boolean;
  isFresh: boolean;
  hasFormResponse: boolean;
  formResponseModifiedDate?: string;
  pdfUploadedDate?: string;
}

export interface JobFormPdfFreshnessResponse {
  forms: FormPdfFreshness[];
}

// Helper function to get category display name
export const getCategoryDisplayName = (category: DocumentCategory | string): string => {
  const categoryMap: Record<number | string, string> = {
    [DocumentCategory.InitialPhotos]: 'Initial Photos',
    [DocumentCategory.DesignRenders]: 'Design Renders',
    [DocumentCategory.Contracts]: 'Contracts',
    [DocumentCategory.Quotes]: 'Quotes',
    [DocumentCategory.Measurements]: 'Measurements',
    [DocumentCategory.InstallationPhotos]: 'Installation Photos',
    [DocumentCategory.Correspondence]: 'Correspondence',
    [DocumentCategory.TechnicalSpecs]: 'Technical Specs',
    [DocumentCategory.FormDocument]: 'Form Document',
    [DocumentCategory.Other]: 'Other',
    'InitialPhotos': 'Initial Photos',
    'DesignRenders': 'Design Renders',
    'Contracts': 'Contracts',
    'Quotes': 'Quotes',
    'Measurements': 'Measurements',
    'InstallationPhotos': 'Installation Photos',
    'Correspondence': 'Correspondence',
    'TechnicalSpecs': 'Technical Specs',
    'FormDocument': 'Form Document',
    'Other': 'Other'
  };
  return categoryMap[category] || 'Unknown';
};

// Helper function to format file size
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Helper function to get file icon based on extension
export const getFileIcon = (extension: string): 'pdf' | 'image' | 'word' | 'excel' | 'file' => {
  const ext = extension.toLowerCase().replace('.', '');
  if (ext === 'pdf') return 'pdf';
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) return 'image';
  if (['doc', 'docx'].includes(ext)) return 'word';
  if (['xls', 'xlsx'].includes(ext)) return 'excel';
  return 'file';
};

/**
 * Normalize DocumentType from API response (string/number) to enum
 */
export const normalizeDocumentType = (value: DocumentType | string | number): DocumentType => {
  if (typeof value === 'number') {
    return value as DocumentType;
  }

  if (typeof value === 'string') {
    switch (value.toLowerCase()) {
      case 'uploaded':
      case '1':
        return DocumentType.Uploaded;
      case 'formpdf':
      case 'form_pdf':
      case '2':
        return DocumentType.FormPdf;
      default:
        return DocumentType.Uploaded; // default fallback
    }
  }

  return DocumentType.Uploaded; // default fallback
};

/**
 * Normalize DocumentCategory from API response (string/number) to enum
 */
export const normalizeDocumentCategory = (value: DocumentCategory | string | number): DocumentCategory => {
  if (typeof value === 'number') {
    return value as DocumentCategory;
  }

  if (typeof value === 'string') {
    switch (value.toLowerCase()) {
      case 'initialphotos':
      case 'initial_photos':
      case '1':
        return DocumentCategory.InitialPhotos;
      case 'designrenders':
      case 'design_renders':
      case '2':
        return DocumentCategory.DesignRenders;
      case 'contracts':
      case '3':
        return DocumentCategory.Contracts;
      case 'quotes':
      case '4':
        return DocumentCategory.Quotes;
      case 'measurements':
      case '5':
        return DocumentCategory.Measurements;
      case 'installationphotos':
      case 'installation_photos':
      case '6':
        return DocumentCategory.InstallationPhotos;
      case 'correspondence':
      case '7':
        return DocumentCategory.Correspondence;
      case 'technicalspecs':
      case 'technical_specs':
      case '8':
        return DocumentCategory.TechnicalSpecs;
      case 'formdocument':
      case 'form_document':
      case '9':
        return DocumentCategory.FormDocument;
      case 'other':
      case '10':
        return DocumentCategory.Other;
      default:
        return DocumentCategory.Other; // default fallback
    }
  }

  return DocumentCategory.Other; // default fallback
};

/**
 * Transform raw API JobDocument to properly typed JobDocument
 */
export const transformJobDocument = (raw: any): JobDocument => {
  return {
    ...raw,
    documentType: normalizeDocumentType(raw.documentType),
    category: normalizeDocumentCategory(raw.category),
    storageTier: raw.storageTier || StorageTier.Hot
  };
};

class DocumentService {
  /**
   * Upload a document for a job
   */
  async uploadDocument(file: File, dto: UploadDocumentDto): Promise<JobDocument> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('jobId', dto.jobId);
    if (dto.category !== undefined) {
      formData.append('category', dto.category.toString());
    }
    if (dto.description) {
      formData.append('description', dto.description);
    }

    const response = await apiClient.post<any>('/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return transformJobDocument(response.data);
  }

  /**
   * Upload multiple documents in a batch
   */
  async batchUploadDocuments(dto: BatchUploadDocumentsDto): Promise<JobDocument[]> {
    const formData = new FormData();
    formData.append('jobId', dto.jobId);

    dto.files.forEach((fileDto, index) => {
      formData.append('files', fileDto.file);
      formData.append(`files[${index}].category`, fileDto.category.toString());
      if (fileDto.description) {
        formData.append(`files[${index}].description`, fileDto.description);
      }
    });

    const response = await apiClient.post<any[]>('/documents/batch-upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.map(transformJobDocument);
  }

  /**
   * Get a document by ID
   */
  async getDocument(id: string): Promise<JobDocument> {
    const response = await apiClient.get<any>(`/documents/${id}`);
    return transformJobDocument(response.data);
  }

  /**
   * Get all documents for a job
   */
  async getDocumentsForJob(jobId: string, forceRefresh = false): Promise<JobDocument[]> {
    const cacheBust = forceRefresh ? `?_t=${Date.now()}` : '';
    const response = await apiClient.get<any[]>(`/documents/job/${jobId}${cacheBust}`);
    return response.data.map(transformJobDocument);
  }

  /**
   * Download a document
   */
  async downloadDocument(id: string): Promise<Blob> {
    const response = await apiClient.get(`/documents/${id}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Open a document in a new tab
   */
  async openDocument(document: JobDocument): Promise<void> {
    const blob = await this.downloadDocument(document.id);
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    // Clean up after a delay
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  }

  /**
   * Download and save a document
   */
  async saveDocument(document: JobDocument): Promise<void> {
    const blob = await this.downloadDocument(document.id);
    const url = URL.createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = document.originalFileName;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Update document metadata
   */
  async updateDocument(dto: UpdateDocumentDto): Promise<JobDocument> {
    const response = await apiClient.patch<any>(`/documents/${dto.id}`, dto);
    return transformJobDocument(response.data);
  }

  /**
   * Delete a document (soft delete)
   */
  async deleteDocument(id: string): Promise<void> {
    await apiClient.delete(`/documents/${id}`);
  }

  /**
   * Permanently delete a document
   */
  async permanentlyDeleteDocument(id: string): Promise<void> {
    await apiClient.delete(`/documents/${id}/permanent`);
  }

  /**
   * Store a form PDF
   */
  async storeFormPdf(dto: StoreFormPdfDto): Promise<JobDocument> {
    const response = await apiClient.post<any>('/documents/form-pdf', dto);
    return transformJobDocument(response.data);
  }

  /**
   * Get form PDF for a job and form
   */
  async getFormPdf(jobId: string, formId: number): Promise<JobDocument | null> {
    try {
      const response = await apiClient.get<any>(`/documents/job/${jobId}/form/${formId}`);
      return transformJobDocument(response.data);
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get thumbnail as data URL for display
   */
  async getThumbnailDataUrl(documentId: string): Promise<string | null> {
    try {
      const response = await apiClient.get(`/documents/thumbnail/${documentId}`, {
        responseType: 'blob',
      });

      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(response.data);
      });
    } catch (error) {
      console.warn('Failed to load thumbnail:', error);
      return null;
    }
  }

  /**
   * Get upload constraints
   */
  async getUploadConstraints(): Promise<UploadConstraints> {
    const response = await apiClient.get<UploadConstraints>('/documents/upload-constraints');
    return response.data;
  }

  /**
   * Validate a file before upload
   */
  async validateFile(file: File, constraints?: UploadConstraints): Promise<{ isValid: boolean; error?: string }> {
    // Get constraints if not provided
    const uploadConstraints = constraints || await this.getUploadConstraints();

    // Check file size
    if (file.size > uploadConstraints.maxFileSizeBytes) {
      return {
        isValid: false,
        error: `File size exceeds the maximum allowed size of ${uploadConstraints.maxFileSizeMb.toFixed(1)} MB.`
      };
    }

    // Check file extension
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!uploadConstraints.allowedExtensions.includes(extension)) {
      return {
        isValid: false,
        error: `File type '${extension}' is not allowed. Allowed types: ${uploadConstraints.allowedExtensions.join(', ')}`
      };
    }

    return { isValid: true };
  }

  /**
   * Get form PDF freshness status for a job
   * Returns freshness info for all printable forms, comparing PDF upload dates against form response modification dates
   */
  async getFormPdfFreshness(jobId: string): Promise<JobFormPdfFreshnessResponse> {
    const response = await apiClient.get<JobFormPdfFreshnessResponse>(`/documents/job/${jobId}/form-pdf-freshness`);
    return response.data;
  }

  /**
   * Validate multiple files for batch upload
   */
  async validateBatchFiles(files: File[], jobId: string, constraints?: UploadConstraints): Promise<{ isValid: boolean; error?: string }> {
    // Get constraints if not provided
    const uploadConstraints = constraints || await this.getUploadConstraints();

    if (files.length === 0) {
      return { isValid: false, error: 'No files selected' };
    }

    if (files.length > 20) {
      return { isValid: false, error: 'Maximum 20 files can be uploaded at once' };
    }

    // Validate each file individually
    for (const file of files) {
      const validation = await this.validateFile(file, uploadConstraints);
      if (!validation.isValid) {
        return validation;
      }
    }

    // Check total size
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > uploadConstraints.maxTotalJobSizeBytes) {
      return {
        isValid: false,
        error: `Total file size exceeds the maximum allowed for this job (${uploadConstraints.maxTotalJobSizeMb.toFixed(1)} MB)`
      };
    }

    return { isValid: true };
  }
}

export const documentService = new DocumentService();
